import { useEffect, useRef, useState } from "react";
import { Box, FormHelperText, InputBase } from "@mui/material";

const EMPTY_DURATION = {
  hours: "",
  minutes: "",
  seconds: "",
};

const parseDuration = (value) => {
  if (!value) {
    return EMPTY_DURATION;
  }

  const match = String(value).match(
    /^(\d{1,3}):([0-5]\d):([0-5]\d)$/
  );

  if (!match) {
    return EMPTY_DURATION;
  }

  return {
    hours: match[1],
    minutes: match[2],
    seconds: match[3],
  };
};

const normalizeDuration = ({ hours, minutes, seconds }) => {
  if (!hours || !minutes || !seconds) {
    return null;
  }

  const hoursNumber = Number(hours);
  const minutesNumber = Number(minutes);
  const secondsNumber = Number(seconds);

  if (
    !Number.isInteger(hoursNumber) ||
    hoursNumber < 0 ||
    hoursNumber > 999 ||
    !Number.isInteger(minutesNumber) ||
    minutesNumber < 0 ||
    minutesNumber > 59 ||
    !Number.isInteger(secondsNumber) ||
    secondsNumber < 0 ||
    secondsNumber > 59
  ) {
    return null;
  }

  return [
    String(hoursNumber).padStart(3, "0"),
    String(minutesNumber).padStart(2, "0"),
    String(secondsNumber).padStart(2, "0"),
  ].join(":");
};

const keepDigits = (value, maxLength) =>
  value.replace(/\D/g, "").slice(0, maxLength);

export const HhhMmSsInput = ({
  value = "",
  onCommit,
  disabled = false,
}) => {
  const [parts, setParts] = useState(() => parseDuration(value));
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(false);

  const hoursRef = useRef(null);
  const minutesRef = useRef(null);
  const secondsRef = useRef(null);

  /*
   * Une réponse du serveur ne doit pas remplacer la valeur
   * pendant que l'utilisateur est encore dans le champ.
   */
  useEffect(() => {
    if (!isEditing) {
      setParts(parseDuration(value));
    }
  }, [value, isEditing]);

  const focusAndSelect = (ref) => {
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.select();
    });
  };

  const commit = () => {
    const isEmpty =
      !parts.hours &&
      !parts.minutes &&
      !parts.seconds;

    if (isEmpty) {
      setError(false);
      return true;
    }

    const normalizedValue = normalizeDuration(parts);

    if (!normalizedValue) {
      setError(true);
      return false;
    }

    const normalizedParts = parseDuration(normalizedValue);

    setParts(normalizedParts);
    setError(false);

    if (normalizedValue !== value) {
      onCommit(normalizedValue);
    }

    return true;
  };

  const handleHoursChange = (event) => {
    const hours = keepDigits(event.target.value, 3);

    setParts((previous) => ({
      ...previous,
      hours,
    }));

    setError(false);

    if (hours.length === 3) {
      focusAndSelect(minutesRef);
    }
  };

  const handleMinuteOrSecondChange = (
    field,
    rawValue,
    nextRef
  ) => {
    const digits = keepDigits(rawValue, 2);

    if (!digits) {
      setParts((previous) => ({
        ...previous,
        [field]: "",
      }));

      setError(false);
      return;
    }

    /*
     * Si le premier chiffre est supérieur à 5,
     * "9" devient directement "09".
     */
    if (digits.length === 1 && Number(digits) > 5) {
      const completedValue = `0${digits}`;

      setParts((previous) => ({
        ...previous,
        [field]: completedValue,
      }));

      setError(false);

      if (nextRef) {
        focusAndSelect(nextRef);
      }

      return;
    }

    if (digits.length === 2 && Number(digits) > 59) {
      setError(true);
      return;
    }

    setParts((previous) => ({
      ...previous,
      [field]: digits,
    }));

    setError(false);

    if (digits.length === 2 && nextRef) {
      focusAndSelect(nextRef);
    }
  };

  const handleSegmentKeyDown = ({
    event,
    currentField,
    previousRef,
    nextRef,
  }) => {
    if (event.key === ":") {
      event.preventDefault();

      if (nextRef) {
        focusAndSelect(nextRef);
      }

      return;
    }

    if (
      event.key === "Backspace" &&
      !parts[currentField] &&
      previousRef
    ) {
      event.preventDefault();
      focusAndSelect(previousRef);
      return;
    }

    if (event.key === "ArrowLeft" && previousRef) {
      event.preventDefault();
      focusAndSelect(previousRef);
      return;
    }

    if (event.key === "ArrowRight" && nextRef) {
      event.preventDefault();
      focusAndSelect(nextRef);
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();

      if (commit()) {
        event.currentTarget.blur();
      }
    }

    if (event.key === "Escape") {
      event.preventDefault();

      setParts(parseDuration(value));
      setError(false);
      event.currentTarget.blur();
    }
  };

  const handlePaste = (event) => {
    const pastedValue = event.clipboardData
      .getData("text")
      .trim();

    const match = pastedValue.match(
      /^(\d{1,3}):(\d{1,2}):(\d{1,2})$/
    );

    if (!match) {
      return;
    }

    const nextParts = {
      hours: match[1],
      minutes: match[2],
      seconds: match[3],
    };

    const normalizedValue = normalizeDuration(nextParts);

    if (!normalizedValue) {
      return;
    }

    event.preventDefault();

    setParts(parseDuration(normalizedValue));
    setError(false);
    focusAndSelect(secondsRef);
  };

  const commonInputProps = {
    inputMode: "numeric",
    autoComplete: "off",
  };

  return (
    <Box>
      <Box
        role="group"
        aria-label="Durée en heures, minutes et secondes"
        onFocus={() => setIsEditing(true)}
        onBlur={(event) => {
          /*
           * Ne pas valider lorsqu'on passe simplement
           * des heures vers les minutes.
           */
          if (event.currentTarget.contains(event.relatedTarget)) {
            return;
          }

          commit();
          setIsEditing(false);
        }}
        onPaste={handlePaste}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 40,
          px: 1,
          border: 1,
          borderColor: error ? "error.main" : "divider",
          borderRadius: 1,
          padding: '7px',
          backgroundColor: disabled
            ? "action.disabledBackground"
            : "#e6e6e6",

          "&:focus-within": {
            borderColor: error ? "error.main" : "primary.main",
            boxShadow: (theme) =>
              `0 0 0 1px ${
                error
                  ? theme.palette.error.main
                  : theme.palette.primary.main
              }`,
          },
        }}
      >
        <InputBase
          inputRef={hoursRef}
          value={parts.hours}
          disabled={disabled}
          placeholder="hhh"
          onFocus={(event) => event.target.select()}
          onChange={handleHoursChange}
          onKeyDown={(event) =>
            handleSegmentKeyDown({
              event,
              currentField: "hours",
              nextRef: minutesRef,
            })
          }
          inputProps={{
            ...commonInputProps,
            maxLength: 3,
            "aria-label": "Heures",
          }}
          sx={{
            width: "4ch",
            "& input": {
              p: 0,
              textAlign: "center",
            },
          }}
        />

        <Box component="span" aria-hidden="true">
          :
        </Box>

        <InputBase
          inputRef={minutesRef}
          value={parts.minutes}
          disabled={disabled}
          placeholder="mm"
          onFocus={(event) => event.target.select()}
          onChange={(event) =>
            handleMinuteOrSecondChange(
              "minutes",
              event.target.value,
              secondsRef
            )
          }
          onKeyDown={(event) =>
            handleSegmentKeyDown({
              event,
              currentField: "minutes",
              previousRef: hoursRef,
              nextRef: secondsRef,
            })
          }
          inputProps={{
            ...commonInputProps,
            maxLength: 2,
            "aria-label": "Minutes",
          }}
          sx={{
            width: "3ch",
            "& input": {
              p: 0,
              textAlign: "center",
            },
          }}
        />

        <Box component="span" aria-hidden="true">
          :
        </Box>

        <InputBase
          inputRef={secondsRef}
          value={parts.seconds}
          disabled={disabled}
          placeholder="ss"
          onFocus={(event) => event.target.select()}
          onChange={(event) =>
            handleMinuteOrSecondChange(
              "seconds",
              event.target.value
            )
          }
          onKeyDown={(event) =>
            handleSegmentKeyDown({
              event,
              currentField: "seconds",
              previousRef: minutesRef,
            })
          }
          inputProps={{
            ...commonInputProps,
            maxLength: 2,
            "aria-label": "Secondes",
          }}
          sx={{
            width: "3ch",
            "& input": {
              p: 0,
              textAlign: "center",
            },
          }}
        />
      </Box>

      {error && (
        <FormHelperText error>
          Saisissez une durée valide au format hhh:mm:ss.
        </FormHelperText>
      )}
    </Box>
  );
};